import cn from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { ChipGroup } from '@/shared/ui';

import {
  CATALOG_OTHER_DEPARTMENT,
  templateDepartmentMessages,
  templateGroupMessages,
  type CatalogFilter,
  type CatalogSource,
} from '../../config';
import {
  filterCatalogItems,
  getCatalogDepartments,
  getCatalogGroups,
  getCatalogSources,
  type CatalogFilterable,
} from '../../lib';

import styles from './TemplateCatalogFilters.module.scss';

const ALL = 'all';

type Props = {
  items: CatalogFilterable[];
  filter: CatalogFilter;
  sources?: CatalogSource[];
  // The visits of a department as chips; a list that groups its items by
  // them shows none.
  groupChips?: boolean;
  // A side panel's look: the departments in one row that scrolls sideways.
  compact?: boolean;
  disabled?: boolean;
  className?: string;
  onChange: (filter: CatalogFilter) => void;
};

export const TemplateCatalogFilters: FC<Props> = ({
  items,
  filter,
  sources = getCatalogSources(items),
  groupChips = true,
  compact = false,
  disabled,
  className,
  onChange,
}) => {
  const { formatMessage } = useIntl();
  const sourceItems = filterCatalogItems(items, {
    source: sources.length > 1 ? filter.source : null,
    department: null,
    group: null,
  });
  const departments = getCatalogDepartments(sourceItems);
  const groups = filter.department
    ? getCatalogGroups(sourceItems, filter.department)
    : [];
  const allLabel = formatMessage({ id: 'global.all', defaultMessage: 'All' });

  const getDepartmentLabel = (department: string) =>
    department in templateDepartmentMessages
      ? formatMessage(templateDepartmentMessages[department])
      : formatMessage({ id: 'global.other', defaultMessage: 'Other' });

  return (
    <div
      className={cn(styles.filters, compact && styles.compact, className)}
      data-component="template-catalog-filters"
    >
      {sources.length > 1 && (
        <ChipGroup<CatalogSource>
          variant="segmented"
          className={styles.sources}
          items={sources.map((source) => ({
            value: source,
            label:
              source === 'saved'
                ? formatMessage({
                    id: 'workspace.templates.mineTab',
                    defaultMessage: 'Saved by clinic',
                  })
                : source === 'partners'
                  ? formatMessage({
                      id: 'transcriptorStart.filter.sourcePartners',
                      defaultMessage: 'Partners',
                    })
                  : formatMessage({
                      id: 'transcriptorStart.filter.sourceOurs',
                      defaultMessage: 'Community',
                    }),
          }))}
          value={filter.source ?? 'ours'}
          label={formatMessage({
            id: 'transcriptorStart.filter.source',
            defaultMessage: 'Template source',
          })}
          disabled={disabled}
          testID="transcriptor-template-source-filter"
          onChange={(source) =>
            onChange({ source, department: null, group: null })
          }
        />
      )}
      {departments.length > 1 && (
        <ChipGroup
          className={styles.departments}
          items={[
            { value: ALL, label: allLabel },
            ...departments.map((department) => ({
              value: department,
              label: getDepartmentLabel(department),
            })),
          ]}
          value={filter.department ?? ALL}
          label={formatMessage({
            id: 'transcriptorStart.filter.department',
            defaultMessage: 'Department',
          })}
          disabled={disabled}
          testID="transcriptor-template-department-filter"
          onChange={(department) =>
            onChange({
              ...filter,
              department: department === ALL ? null : department,
              group: null,
            })
          }
        />
      )}
      {groupChips &&
        groups.length > 1 &&
        filter.department !== CATALOG_OTHER_DEPARTMENT && (
          <ChipGroup
            items={[
              { value: ALL, label: allLabel },
              ...groups.map((group) => ({
                value: group,
                label: formatMessage(templateGroupMessages[group]),
              })),
            ]}
            value={filter.group ?? ALL}
            label={formatMessage({
              id: 'transcriptorStart.filter.group',
              defaultMessage: 'Visits of the department',
            })}
            disabled={disabled}
            testID="transcriptor-template-group-filter"
            onChange={(group) =>
              onChange({ ...filter, group: group === ALL ? null : group })
            }
          />
        )}
    </div>
  );
};
